
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CreateSundayMarketPriceInput, SundayMarketPrice, SundayMarketPriceFilters } from '@/types/database/sunday-market-prices';
import { useAuth } from '@/contexts/AuthContext';

export const useSundayMarketPrices = (filters: SundayMarketPriceFilters = {}) => {
  return useQuery({
    queryKey: ['sunday-market-prices', filters],
    queryFn: async () => {
      let query = supabase
        .from('sunday_market_prices')
        .select('*')
        .order('date', { ascending: false });

      if (filters.product_name) {
        query = query.ilike('product_name', `%${filters.product_name}%`);
      }

      if (filters.location) {
        query = query.ilike('location', `%${filters.location}%`);
      }

      if (filters.date_from) {
        query = query.gte('date', filters.date_from);
      }

      if (filters.date_to) {
        query = query.lte('date', filters.date_to);
      }

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return data as SundayMarketPrice[];
    },
  });
};

export const useCreateSundayMarketPrice = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (price: CreateSundayMarketPriceInput) => {
      const { data, error } = await supabase
        .from('sunday_market_prices')
        .insert([{ ...price, created_by: user?.id }])
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sunday-market-prices'] });
    },
  });
};

export const useUpdateSundayMarketPrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, price }: { id: string; price: CreateSundayMarketPriceInput }) => {
      const { data, error } = await supabase
        .from('sunday_market_prices')
        .update(price)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sunday-market-prices'] });
    },
  });
};

export const useDeleteSundayMarketPrice = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('sunday_market_prices')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sunday-market-prices'] });
    },
  });
};

export const useBulkCreateSundayMarketPrices = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (prices: CreateSundayMarketPriceInput[]) => {
      const pricesWithUser = prices.map(price => ({
        ...price,
        created_by: user?.id,
      }));

      const { data, error } = await supabase
        .from('sunday_market_prices')
        .insert(pricesWithUser)
        .select();

      if (error) {
        throw error;
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sunday-market-prices'] });
    },
  });
};
